import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import './App.css';
import { fetchPools, fetchTrending } from './api';
import { ThemeProvider, useTheme } from './theme';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import Navbar from './components/Navbar';
import Ticker from './components/Ticker';
import TrendingBar from './components/TrendingBar';
import TrendingView from './components/TrendingView';
import StatCard from './components/StatCard';
import FilterBar from './components/FilterBar';
import PoolTable from './components/PoolTable';
import DetailPanel from './components/DetailPanel';
import StablecoinsView from './components/StablecoinsView';
import BestPicksView from './components/BestPicksView';
import MemecoinsView from './components/MemecoinsView';
import PortfolioView from './components/PortfolioView';
import ILCalculatorView from './components/ILCalculatorView';
import FarmCalculatorView from './components/FarmCalculatorView';
import HeatmapView from './components/HeatmapView';
import ComparatorView from './components/ComparatorView';
import CorrelationView from './components/CorrelationView';
import WalletAnalyzerView from './components/WalletAnalyzerView';
import SwapView from './components/SwapView';
import PoolDetailPage from './components/PoolDetailPage';
// ShareButton used inside DetailPanel and PoolDetailPage
import { WalletProvider } from './contexts/WalletContext';
import { AlertsProvider, useAlerts } from './contexts/AlertsContext';
import { ProProvider, usePro } from './contexts/ProContext';
import UpgradeModal from './components/UpgradeModal';
import FixedIncomeCompareView from './components/FixedIncomeCompareView';
import AlertsProPanel from './components/AlertsProPanel';
import ProWalletAnalyzerView from './components/ProWalletAnalyzerView';
import PositionReportView from './components/PositionReportView';
import SmartRebalancingView from './components/SmartRebalancingView';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import { exportCSV, exportJSON } from './utils/exportData';
import { fmt } from './utils';
import { useIsMobile } from './useMediaQuery';

// Route-to-view mapping
const VIEW_ROUTES = {
  '/': 'explorer',
  '/degen': 'memecoins',
  '/top-picks': 'bestpicks',
  '/market': 'trending',
  '/safe-yields': 'stables',
  '/portfolio': 'portfolio',
  '/swap': 'swap',
  '/heatmap': 'heatmap',
  '/correlation': 'correlation',
  '/compare': 'compare',
  '/farm-calc': 'farmcalc',
  '/il-calc': 'ilcalc',
  '/analyzer': 'wallet',
  '/compare-rates': 'compareRates',
  '/pro-alerts': 'proAlerts',
  '/pro-analyzer': 'proAnalyzer',
  '/report': 'report',
  '/rebalancing': 'rebalancing',
};

const VIEW_TO_ROUTE = Object.fromEntries(
  Object.entries(VIEW_ROUTES).map(([route, view]) => [view, route])
);

function SwapBanner({ onClick, isMobile, colors, dark }) {
  return (
    <>
      <style>{`
        @keyframes swapBannerGradient {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes swapShimmer {
          0%   { left: -30%; }
          100% { left: 130%; }
        }
        @keyframes swapDotPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(0.85); }
        }
      `}</style>
      <div
        onClick={onClick}
        style={{
          margin: isMobile ? "0 12px 8px" : "0 24px 10px",
          padding: isMobile ? "10px 14px" : "10px 20px",
          background: dark
            ? "linear-gradient(135deg, #0A2E1A 0%, #0E3D24 50%, #0A2E1A 100%)"
            : "linear-gradient(135deg, #E8F8F2 0%, #D8F2E8 35%, #E2F6EE 65%, #E8F8F2 100%)",
          backgroundSize: "200% 200%",
          animation: "swapBannerGradient 8s ease infinite",
          border: `1px solid ${dark ? "rgba(168,222,200,0.15)" : "#A8DEC8"}`,
          borderLeft: `3px solid ${dark ? "#2EBF7B" : "#0D7A4E"}`,
          borderRadius: 8,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.boxShadow = dark
            ? "0 2px 12px rgba(46,191,123,0.12)"
            : "0 2px 12px rgba(13,122,78,0.10)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {/* Shimmer sweep */}
        <div style={{
          position: "absolute",
          top: 0,
          left: "-30%",
          width: "20%",
          height: "100%",
          background: dark
            ? "linear-gradient(90deg, transparent 0%, rgba(168,222,200,0.06) 50%, transparent 100%)"
            : "linear-gradient(90deg, transparent 0%, rgba(13,122,78,0.06) 50%, transparent 100%)",
          transform: "skewX(-15deg)",
          animation: "swapShimmer 5s ease-in-out infinite",
          pointerEvents: "none",
        }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, position: "relative", zIndex: 1 }}>
          {/* Pulsing live dot */}
          <span style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: dark ? "#2EBF7B" : "#0D7A4E",
            flexShrink: 0,
            display: "inline-block",
            animation: "swapDotPulse 2s ease-in-out infinite",
            boxShadow: dark ? "0 0 4px rgba(46,191,123,0.4)" : "0 0 4px rgba(13,122,78,0.3)",
          }} />
          <span style={{ fontSize: 15, flexShrink: 0, opacity: 0.85 }}>⇄</span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: isMobile ? 11 : 12,
            color: dark ? "#A8DEC8" : "#0D7A4E",
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            Swap tokens at 0.5% fee — 43% less than MetaMask
          </span>
        </div>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          fontWeight: 600,
          color: "#FFFFFF",
          background: dark
            ? "linear-gradient(135deg, #1A8A56 0%, #0D7A4E 100%)"
            : "linear-gradient(135deg, #0D7A4E 0%, #0A6640 100%)",
          padding: "4px 12px",
          borderRadius: 4,
          whiteSpace: "nowrap",
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
          letterSpacing: 0.3,
        }}>
          Swap →
        </span>
      </div>
    </>
  );
}

function ProBanner({ onClick, isMobile, colors, dark, isPro }) {
  if (isPro) return null;

  const proFeatures = [
    { icon: "📊", label: "Multi-Protocol Analyzer" },
    { icon: "📈", label: "DeFi vs TradFi Compare" },
    { icon: "🔔", label: "Email & Webhook Alerts" },
    { icon: "📋", label: "Position Reports + PDF" },
    { icon: "⚡", label: "Smart Rebalancing" },
  ];

  const orbs = [
    { size: 14, top: '10%', left: '8%', delay: 0, dur: 4.5 },
    { size: 8, top: '65%', left: '20%', delay: 1, dur: 5 },
    { size: 16, top: '15%', left: '75%', delay: 0.6, dur: 3.8 },
    { size: 7, top: '70%', left: '90%', delay: 1.8, dur: 4.2 },
    { size: 11, top: '45%', left: '55%', delay: 0.3, dur: 5.5 },
    { size: 9, top: '80%', left: '45%', delay: 2.2, dur: 4 },
  ];

  return (
    <>
      <style>{`
        @keyframes proBannerGradient {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes proOrb {
          0%   { transform: translateY(0px); opacity: 0.2; }
          50%  { transform: translateY(-16px); opacity: 0.55; }
          100% { transform: translateY(0px); opacity: 0.2; }
        }
        @keyframes proShimmer {
          0%   { left: -30%; }
          100% { left: 130%; }
        }
        @keyframes proCrownFloat {
          0%   { transform: translateY(0px) rotate(0deg); }
          25%  { transform: translateY(-4px) rotate(3deg); }
          50%  { transform: translateY(0px) rotate(0deg); }
          75%  { transform: translateY(-2px) rotate(-3deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        @keyframes proCtaGlow {
          0%   { box-shadow: 0 0 0 0 rgba(224,204,112,0.4); }
          50%  { box-shadow: 0 0 20px 4px rgba(224,204,112,0.2); }
          100% { box-shadow: 0 0 0 0 rgba(224,204,112,0.4); }
        }
        @keyframes proFeatureScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={onClick}
        style={{
          margin: isMobile ? "0 12px 12px" : "0 24px 16px",
          padding: isMobile ? "22px 18px" : "28px 36px",
          background: dark
            ? "linear-gradient(135deg, #2A1F00 0%, #3D2E00 25%, #4A3800 50%, #2A1F00 75%, #3D2E00 100%)"
            : "linear-gradient(135deg, #8A6A00 0%, #B8901A 25%, #A07A10 50%, #8A6A00 75%, #C49A20 100%)",
          backgroundSize: "300% 300%",
          animation: "proBannerGradient 6s ease infinite",
          borderRadius: 14,
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          boxShadow: dark
            ? "0 6px 28px rgba(224,204,112,0.15)"
            : "0 6px 28px rgba(138,106,0,0.25)",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.boxShadow = dark
            ? "0 10px 40px rgba(224,204,112,0.30)"
            : "0 10px 40px rgba(138,106,0,0.40)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = dark
            ? "0 6px 28px rgba(224,204,112,0.15)"
            : "0 6px 28px rgba(138,106,0,0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Shimmer sweep */}
        <div style={{
          position: "absolute",
          top: 0,
          left: "-30%",
          width: "25%",
          height: "100%",
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)",
          transform: "skewX(-15deg)",
          animation: "proShimmer 3.5s ease-in-out infinite",
          pointerEvents: "none",
          zIndex: 1,
        }} />

        {/* Floating orbs */}
        {orbs.map((orb, i) => (
          <div key={i} style={{
            position: "absolute",
            top: orb.top,
            left: orb.left,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: "rgba(224,204,112,0.35)",
            boxShadow: "0 0 10px rgba(224,204,112,0.25)",
            animation: `proOrb ${orb.dur}s ease-in-out ${orb.delay}s infinite`,
            pointerEvents: "none",
            zIndex: 0,
          }} />
        ))}

        {/* Decorative large circles */}
        <div style={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute",
          bottom: -35,
          right: 90,
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.03)",
          pointerEvents: "none",
        }} />

        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          position: "relative",
          zIndex: 2,
        }}>
          {/* Title row */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{
                fontSize: isMobile ? 24 : 30,
                animation: "proCrownFloat 3s ease-in-out infinite",
                display: "inline-block",
                filter: "drop-shadow(0 0 8px rgba(224,204,112,0.5))",
              }}>
                👑
              </span>
              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: isMobile ? 22 : 26,
                color: "#FFFFFF",
                lineHeight: 1.2,
                textShadow: "0 2px 8px rgba(0,0,0,0.25)",
              }}>
                PoolScope Pro
              </div>
            </div>

            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: isMobile ? 12 : 13,
              color: "rgba(255,255,255,0.75)",
              marginBottom: 16,
              lineHeight: 1.4,
            }}>
              Professional DeFi portfolio management
            </div>

            {/* Feature pills */}
            <div style={{
              overflow: "hidden",
              marginBottom: 6,
              width: "100%",
            }}>
              <div style={{
                display: "flex",
                gap: 10,
                ...(isMobile ? {
                  animation: "proFeatureScroll 20s linear infinite",
                  width: "max-content",
                } : {
                  flexWrap: "wrap",
                }),
              }}>
                {(isMobile ? [...proFeatures, ...proFeatures] : proFeatures).map((feat, i) => (
                  <span key={i} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#FFFFFF",
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(224,204,112,0.35)",
                    borderRadius: 20,
                    padding: "5px 12px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    boxShadow: "0 0 6px rgba(224,204,112,0.1)",
                  }}>
                    <span style={{ fontSize: 13 }}>{feat.icon}</span>
                    {feat.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Price block + CTA button */}
          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 14 : 20,
          }}>
            {/* Price block */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              background: "rgba(0,0,0,0.18)",
              borderRadius: 12,
              padding: isMobile ? "12px 16px" : "14px 22px",
              border: "1px solid rgba(224,204,112,0.25)",
              boxShadow: "0 0 20px rgba(224,204,112,0.08), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: isMobile ? 30 : 34,
                    color: "#FFD700",
                    lineHeight: 1,
                    textShadow: "0 0 16px rgba(224,204,112,0.4)",
                  }}>
                    $9.90
                  </span>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    fontWeight: 500,
                  }}>
                    /month
                  </span>
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: "rgba(255,255,255,0.4)",
                  marginTop: 4,
                }}>
                  or $99/year (save 17%)
                </div>
              </div>
              <span style={{
                fontSize: 9,
                letterSpacing: 1,
                fontWeight: 600,
                color: '#FFFFFF',
                background: 'rgba(224,204,112,0.3)',
                border: '1px solid rgba(224,204,112,0.4)',
                borderRadius: 3,
                padding: '2px 8px',
                fontFamily: "'IBM Plex Mono', monospace",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
                marginTop: 2,
              }}>
                LAUNCH PRICE
              </span>
            </div>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 28px rgba(224,204,112,0.55), 0 0 8px rgba(255,255,255,0.3)" }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              onClick={(e) => { e.stopPropagation(); onClick(); }}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 13,
                fontWeight: 700,
                color: "#FFFFFF",
                background: "linear-gradient(135deg, #B8901A 0%, #8A6A00 100%)",
                border: "1px solid rgba(224,204,112,0.5)",
                borderRadius: 8,
                padding: isMobile ? "12px 24px" : "14px 36px",
                letterSpacing: 0.8,
                whiteSpace: "nowrap",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(138,106,0,0.3)",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 8,
                outline: "none",
                animation: "proCtaGlow 3s ease-in-out infinite",
              }}
            >
              Upgrade to Pro
              <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

function AppContent() {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkAlerts } = useAlerts();
  const { isPro } = usePro();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derive view from current route
  const view = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/pool/')) return 'poolDetail';
    return VIEW_ROUTES[path] || 'explorer';
  }, [location.pathname]);

  // Explorer state — sync with URL search params (feature 3.3)
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [stablesOnly, setStablesOnly] = useState(searchParams.get('stables') === 'true');
  const [chain, setChain] = useState(searchParams.get('chain') || 'All');
  const [protocol, setProtocol] = useState(searchParams.get('protocol') || 'All');
  const [sortBy, setSortBy] = useState('popular');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedPool, setSelectedPool] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [trending, setTrending] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showAnomalous, setShowAnomalous] = useState(false);

  const searchInputRef = useRef(null);

  // Update URL params when filters change (only on explorer view)
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  useEffect(() => {
    if (view !== 'explorer') return;
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (stablesOnly) params.set('stables', 'true');
    if (chain !== 'All') params.set('chain', chain);
    if (protocol !== 'All') params.set('protocol', protocol);
    const newSearch = params.toString();
    const currentSearch = searchParamsRef.current.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, search, stablesOnly, chain, protocol]);

  // Navigate helper that maps view keys to routes
  const setView = useCallback((viewKey) => {
    const route = VIEW_TO_ROUTE[viewKey] || '/';
    navigate(route);
  }, [navigate]);

  const refreshData = useCallback(() => {
    fetchPools()
      .then(data => {
        setPools(data);
        setLastUpdated(new Date());
        checkAlerts(data);
        toast.success(`${data.length} pools updated`, {
          style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
          duration: 2000,
        });
      })
      .catch(err => {
        console.warn("Auto-refresh failed:", err.message);
        toast.error("Refresh failed", {
          style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
          duration: 3000,
        });
      });

    fetchTrending()
      .then(data => setTrending(data))
      .catch(err => console.warn("Trending refresh failed:", err.message));
  }, [checkAlerts]);

  useEffect(() => {
    // Initial load
    fetchPools()
      .then(data => {
        setPools(data);
        setLoading(false);
        setLastUpdated(new Date());
        checkAlerts(data);
        toast.success(`Loaded ${data.length} pools`, {
          style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
          duration: 2500,
          icon: null,
        });
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        toast.error(`Failed: ${err.message}`, {
          style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
          duration: 4000,
        });
      });

    fetchTrending()
      .then(data => setTrending(data))
      .catch(err => console.warn("Trending fetch failed:", err.message));

    // Auto-refresh every 5 minutes
    const interval = setInterval(refreshData, 300000);
    return () => clearInterval(interval);
  }, [refreshData, checkAlerts]);

  const filtered = useMemo(() => {
    let result = pools;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.tokens.some(t => t.toLowerCase().includes(q)) ||
        p.protocol.toLowerCase().includes(q) ||
        p.chain.toLowerCase().includes(q)
      );
    }

    if (!showAnomalous) result = result.filter(p => !p.anomalous);
    if (stablesOnly) result = result.filter(p => p.stablecoin);
    if (chain !== 'All') result = result.filter(p => p.chain === chain);
    if (protocol !== 'All') result = result.filter(p => p.protocol === protocol);

    const riskValue = { LOW: 1, MED: 2, HIGH: 3 };
    return [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'popular') {
        aVal = (a.volume24h || 0) + (a.fees24h || 0) * 10;
        bVal = (b.volume24h || 0) + (b.fees24h || 0) * 10;
        if (aVal === bVal) {
          aVal = a.tvl || 0;
          bVal = b.tvl || 0;
        }
      } else if (sortBy === 'risk') {
        aVal = riskValue[a.risk] || 0;
        bVal = riskValue[b.risk] || 0;
      } else {
        aVal = a[sortBy];
        bVal = b[sortBy];
      }
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [pools, search, stablesOnly, chain, protocol, sortBy, sortDir, showAnomalous]);

  useEffect(() => {
    setVisibleCount(50);
  }, [search, stablesOnly, chain, protocol, sortBy, sortDir, showAnomalous]);

  const handleSort = useCallback((col) => {
    if (col === sortBy) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  }, [sortBy]);

  // Keyboard shortcuts — use filtered list so j/k navigate visible rows
  useKeyboardShortcuts({
    pools: filtered.slice(0, visibleCount),
    selectedPool,
    onSelectPool: setSelectedPool,
    searchInputRef,
    onClosePanel: () => setSelectedPool(null),
  });

  const stats = useMemo(() => {
    if (pools.length === 0) return { totalTvl: 0, avgApr: 0, bestPool: null };
    const totalTvl = pools.reduce((s, p) => s + p.tvl, 0);
    const avgApr = pools.reduce((s, p) => s + p.apr, 0) / pools.length;
    const bestPool = pools.reduce((best, p) => p.apr > best.apr ? p : best, pools[0]);
    return { totalTvl, avgApr, bestPool };
  }, [pools]);

  // Export handlers
  const handleExportCSV = useCallback(() => {
    exportCSV(filtered, `poolscope-${view}`);
    toast.success('CSV exported', {
      style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
      duration: 2000,
    });
  }, [filtered, view]);

  const handleExportJSON = useCallback(() => {
    exportJSON(filtered, `poolscope-${view}`);
    toast.success('JSON exported', {
      style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
      duration: 2000,
    });
  }, [filtered, view]);

  // Common detail panel with swap navigation
  const renderDetailPanel = () => (
    <DetailPanel
      pool={selectedPool}
      onClose={() => setSelectedPool(null)}
      onSwap={(sell, buy) => { setSelectedPool(null); navigate('/swap'); }}
      onViewDetail={selectedPool ? () => { setSelectedPool(null); navigate(`/pool/${selectedPool.id}`); } : undefined}
    />
  );

  const explorerContent = (
    <>
      <Ticker pools={pools.slice(0, 20)} onSelectPool={setSelectedPool} />

      <div style={{
        display: "flex",
        gap: isMobile ? 12 : 16,
        padding: isMobile ? "12px 12px" : "20px 24px",
        flexWrap: "wrap",
        flexDirection: isMobile ? "column" : "row",
      }}>
        <StatCard
          label="Total Value Locked"
          value={fmt(stats.totalTvl)}
          sub={`across ${pools.length} pools`}
          accent="green"
          tooltip="Total Value Locked \u2014 the total amount of assets deposited across all tracked pools. Higher TVL generally means more liquidity and lower slippage."
        />
        <StatCard
          label="Average APR"
          value={`${stats.avgApr.toFixed(2)}%`}
          sub="all tracked pools"
          accent="gold"
          tooltip="Annual Percentage Rate \u2014 the average yearly return across all tracked pools, without compounding."
        />
        {stats.bestPool && (
          <StatCard
            label="Best Yield"
            value={`${stats.bestPool.apr.toFixed(2)}%`}
            sub={`${stats.bestPool.name} on ${stats.bestPool.chain}`}
            accent="coral"
            tooltip="The highest APR currently available among all tracked pools."
            onClick={() => setSelectedPool(stats.bestPool)}
            tokens={stats.bestPool.tokens}
          />
        )}
      </div>

      {/* Pro Upgrade Banner */}
      <ProBanner onClick={() => setShowUpgradeModal(true)} isMobile={isMobile} colors={colors} dark={dark} isPro={isPro} />

      {/* Swap Promotion Banner */}
      <SwapBanner onClick={() => navigate('/swap')} isMobile={isMobile} colors={colors} dark={dark} />

      <div
        onClick={() => navigate('/analyzer')}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          margin: isMobile ? "0 12px 12px" : "0 24px 16px",
          padding: "14px 20px",
          background: "#FFFFFF",
          borderRadius: 12,
          border: "1px solid #E8EAED",
          borderLeft: "3px solid #8247E5",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          cursor: "pointer",
          transition: "box-shadow 0.15s ease",
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(130,71,229,0.10)"}
        onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"}
      >
        <span style={{ fontSize: 22 }} role="img" aria-label="wallet">&#x1F45B;</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 18,
            color: "#111111",
            lineHeight: 1.2,
          }}>
            Wallet Analyzer
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: "#888888",
            marginTop: 2,
          }}>
            Analyze any wallet&apos;s Uniswap V3 positions
          </div>
        </div>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          fontWeight: 600,
          color: "#8247E5",
          letterSpacing: 0.5,
        }}>
          OPEN &rarr;
        </span>
      </div>

      <FilterBar
        search={search} setSearch={setSearch}
        stablesOnly={stablesOnly} setStablesOnly={setStablesOnly}
        chain={chain} setChain={setChain}
        protocol={protocol} setProtocol={setProtocol}
        sortBy={sortBy} setSortBy={handleSort}
        sortDir={sortDir}
        pools={pools}
        showAnomalous={showAnomalous}
        onToggleAnomalous={() => setShowAnomalous(v => !v)}
        searchInputRef={searchInputRef}
        onExportCSV={handleExportCSV}
        onExportJSON={handleExportJSON}
      />

      <PoolTable
        pools={filtered.slice(0, visibleCount)}
        selectedId={selectedPool?.id}
        onSelect={setSelectedPool}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      {visibleCount < filtered.length && (
        <div style={{ textAlign: "center", padding: "16px 24px 32px" }}>
          <button
            onClick={() => setVisibleCount(c => c + 50)}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              color: colors.green,
              background: colors.greenBg,
              border: `1px solid ${colors.greenBorder}`,
              borderRadius: 8,
              padding: "10px 28px",
              cursor: "pointer",
              letterSpacing: 0.5,
            }}
          >
            Show more ({filtered.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {renderDetailPanel()}
    </>
  );

  return (
    <div style={{ minHeight: "100vh", background: colors.bg, transition: "background-color 0.3s ease" }}>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: colors.surface,
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            boxShadow: `0 8px 24px ${colors.shadow}`,
          },
        }}
      />

      {showUpgradeModal && <UpgradeModal open onClose={() => setShowUpgradeModal(false)} />}

      <Navbar
        view={view}
        setView={setView}
        poolCounts={{
          explorer: pools.length,
          stables: pools.filter(p => p.stablecoin).length,
          bestpicks: Math.min(30, pools.length),
        }}
        lastUpdated={lastUpdated}
        onRefresh={refreshData}
      />

      {!loading && !error && pools.length > 0 && view !== 'memecoins' && view !== 'poolDetail' && (
        <TrendingBar pools={pools} onSelectPool={setSelectedPool} />
      )}

      {loading && (
        <div style={{
          textAlign: "center",
          padding: "80px 0",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          color: colors.labelMuted,
        }}>
          Loading pools from DeFi Llama...
        </div>
      )}

      {error && (
        <div style={{
          textAlign: "center",
          padding: "80px 0",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          color: colors.red,
        }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <Routes>
          <Route path="/" element={explorerContent} />
          <Route path="/pool/:id" element={<PoolDetailPage pools={pools} />} />
          <Route path="/degen" element={
            <>
              <MemecoinsView pools={pools} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/top-picks" element={
            <>
              <BestPicksView pools={pools} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/market" element={
            <>
              <TrendingView pools={pools} trending={trending} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/safe-yields" element={
            <>
              <StablecoinsView pools={pools} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/portfolio" element={
            <>
              <PortfolioView pools={pools} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/swap" element={<SwapView />} />
          <Route path="/heatmap" element={
            <>
              <HeatmapView pools={pools} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/correlation" element={
            <>
              <CorrelationView pools={pools} onSelectPool={setSelectedPool} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/compare" element={<ComparatorView pools={pools} />} />
          <Route path="/farm-calc" element={<FarmCalculatorView pools={pools} />} />
          <Route path="/il-calc" element={
            <>
              <ILCalculatorView pools={pools} />
              {renderDetailPanel()}
            </>
          } />
          <Route path="/analyzer" element={<WalletAnalyzerView />} />
          <Route path="/compare-rates" element={<FixedIncomeCompareView pools={pools} />} />
          <Route path="/pro-alerts" element={<AlertsProPanel pools={pools} />} />
          <Route path="/pro-analyzer" element={<ProWalletAnalyzerView pools={pools} />} />
          <Route path="/report" element={<PositionReportView />} />
          <Route path="/rebalancing" element={<SmartRebalancingView pools={pools} />} />
          {/* Fallback: redirect unknown paths to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <WalletProvider>
        <ProProvider>
          <AlertsProvider>
            <AppContent />
          </AlertsProvider>
        </ProProvider>
      </WalletProvider>
    </ThemeProvider>
  );
}

export default App;
